<script setup lang="ts">
import { ColorPicker } from '@/components/color-picker';
import { SvgColorPalette } from '@repo/icons';

import { type ColorPalette, useFabricStore } from '@/store/fabric.ts';

import type { FabricPath } from '@repo/style3d';
import { getFabricPathConfigure } from '@repo/style3d';

import { useRelationStore, useSelectionStore, useStyle3dStore } from '@repo/stores';
import { type Shape, useStyle3d } from '@repo/style3d';
import { useParamStore } from '@/store/param.ts';
import { useRouter } from 'vue-router';
import { getImageColorChangedByMask } from '@repo/opencv';
import { type FabricShareInfo } from '@repo/style3d';
import axios, { type AxiosResponse } from 'axios';
import { getAppConfig } from '@/utils/env';
import { joinUrl, rgbToHex, getMMNum } from '@repo/utils';
import { computed } from 'vue';

const defaultPalette = [
  {
    colorNo: '1',
    color: '#A9D8F6'
  },
  {
    colorNo: '2',
    color: `#CEE2E0`
  },
  {
    colorNo: '3',
    color: `#F9F0EB`
  },
  {
    colorNo: '4',
    color: `#D69F98`
  },
  {
    colorNo: '5',
    color: `#B7ABB9`
  }
];
const { OSS_BASE_URL, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const fabricStore = useFabricStore();
const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const paramStore = useParamStore();
const style3dStore = useStyle3dStore();

const router = useRouter();

// 获取当前选中面料的materialIndex
const currentMaterialIndex = computed(() => {
  const selectedFabricId = selectionStore.selectedFabricId;
  if (!selectedFabricId) return 0;
  
  const found = relationStore.shapeList.find(item => item.id === selectedFabricId);
  return found?.materialIndex ?? 0;
});

// 根据当前选中的materialIndex获取对应的面料信息
const currentFabricInfo = computed(() => {
  const materialIndex = currentMaterialIndex.value;
  const fabricInfo = fabricStore.getFabricInfoByMaterialIndex(materialIndex);
  
  // 如果是materialIndex=0且没有在map中，使用主fabricInfo
  if (materialIndex === 0 && !fabricInfo) {
    return fabricStore.fabricInfo;
  }
  
  // 其他情况直接返回（可能是undefined）
  return fabricInfo;
});

// 使用当前面料的palette
const currentPalette = computed(() => {
  const fabricInfo = currentFabricInfo.value;
  if (!fabricInfo?.info?.palette) {
    return defaultPalette;
  }
  
  return fabricInfo.info.palette.map(item => ({
    ...item,
    color: rgbToHex(item.color.R, item.color.G, item.color.B)
  }));
});
const updateUrlQuery = (shape: Shape, c: Pick<ColorPalette, 'color'> & Partial<ColorPalette>, regionColors?: string[]) => {
  const { type: fabricType, id: fabricId, fabricIds } = paramStore.resourceParam.fabricResource;
  
  // 获取当前 shape 的 materialIndex
  const materialIndex = shape.materialIndex ?? 0;
  
  // 根据 materialIndex 获取对应的 fabricId
  const expectedFabricId = materialIndex === 0 ? fabricId : (fabricIds?.[materialIndex] ?? fabricId);
  
  console.log('[fabric-color] updateUrlQuery 检查:', {
    shapeFabric: shape.fabric,
    expectedFabricType: fabricType,
    expectedFabricId,
    materialIndex,
    fabricIds,
    mainFabricId: fabricId
  });
  
  if (shape.fabric?.type === fabricType && shape.fabric?.id === expectedFabricId) {
    // 更新对应materialIndex的颜色
    
    // 获取当前路由中的colors参数
    const currentColorsParam = router.currentRoute.value.query.colors as string | undefined;
    let colorsMap: Record<number, { colorNo?: string, color: string, regionColors?: string[] }> = {};
    
    // 如果已有colors参数，解析它
    if (currentColorsParam) {
      try {
        colorsMap = JSON.parse(decodeURIComponent(currentColorsParam));
      } catch (e) {
        console.error('[fabric-color] 解析colors参数失败:', e);
      }
    }
    
    // 更新对应materialIndex的颜色
    // 如果有区域颜色数组，则保存完整的区域颜色信息
    if (regionColors && regionColors.length > 0) {
      colorsMap[materialIndex] = {
        colorNo: c.colorNo,
        color: c.color,
        regionColors: regionColors
      };
    } else {
      colorsMap[materialIndex] = {
        colorNo: c.colorNo,
        color: c.color,
        regionColors: undefined
      };
    }
    
    // 编码并更新URL
    const colors = encodeURIComponent(JSON.stringify(colorsMap));
    router.replace({
      query: {
        ...router.currentRoute.value.query,
        colors,
        // 移除旧的color参数（向后兼容）
        color: undefined
      }
    });
    
    console.log('[fabric-color] 更新URL颜色参数:', {
      materialIndex,
      color: c,
      regionColors,
      allColors: colorsMap
    });
  } else {
    console.warn('[fabric-color] updateUrlQuery 条件不通过，未更新URL:', {
      reason: 'fabric 信息不匹配',
      shapeFabric: shape.fabric,
      expectedType: fabricType,
      expectedId: expectedFabricId,
      materialIndex
    });
  }
};

const { changeFabricColor, selectMaterial, changeFabric } = useStyle3d();

const onClickColor = async (c: Pick<ColorPalette, 'color'> & Partial<ColorPalette>) => {
  const { selectedFabricId } = selectionStore;
  const found = relationStore.shapeList.find(item => item.id === selectedFabricId);
  
  // 保存原始选中的fabricId，用于后续恢复
  const originalSelectedFabricId = selectedFabricId;
  
  if (selectedFabricId === '' || selectedFabricId === null || found) {
    const materialIndex = found?.materialIndex;
    const filteredList = relationStore.shapeList.filter(item => {
      if (item.type !== 'main')
        return false;
      if (materialIndex === undefined) {
        return item.materialIndex === 0 || item.materialIndex === undefined;
      }
      return item.materialIndex === materialIndex;
    });

    if (!filteredList?.length) {
      return;
    }

    const changeColorUrlMap: Map<string, string> = new Map();
    let finalRegionColors: string[] | undefined = undefined;

    const updatedList: Shape[] = [];
    for (const item of filteredList) {
      await selectMaterial(item.id);

      const imgUrl = item.name; //面料对应的图片
      let localChangedColorUrl: string|undefined = undefined;
      if (changeColorUrlMap.has(imgUrl)) {
        localChangedColorUrl = changeColorUrlMap.get(imgUrl);
      } else {
        //从info.json判断有无mask图,有则支持按区域变色
        let hasMask = false;
        const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;
        const fabric:string|undefined = item.fabric?.id;
        if (fabric){
          const fabricRoot = joinUrl(fabricRootPath, fabric);
          const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
          hasMask = !!infoResult.data.mask.srcImage;
          if (hasMask){
            //const localUrl = await getValidImageBase64(imgUrl, "", "png"); //本地图片
            //const imgData :ImageData = await changeColor(localUrl, c.color); //本地图片变色处理
            
            // 重要：mask 和 srcImage 必须使用原始尺寸，不能通过 OSS 缩放
            // 因为 OSS 缩放可能会破坏它们之间的像素级对齐关系
            let maskImageUrls:string[] = [];
            for (let i = 0;i < infoResult.data.mask.list.length;++i){
              maskImageUrls.push(joinUrl(fabricRoot, 'mask', infoResult.data.mask.list[i]!));
            }
            const maskInfoJsonUrl:string = joinUrl(fabricRoot, 'mask', "mask_info.json");
            
            // 先获取当前所有区域的颜色
            let strColors:string[] = [...(currentFabricInfo.value?.mask?.colors || infoResult.data.mask.colors!)];
            
            // 根据选中的区域索引来改变对应区域的颜色
            const itemMaterialIndex = item.materialIndex ?? 0;
            const selectedIndex = fabricStore.getSelectedRegionIndex(itemMaterialIndex);
            strColors[selectedIndex] = c.color;
            
            // 先更新 fabricInfo 中的颜色数组，使 fabric-region-color.vue 显示新颜色
            const materialIndex = item.materialIndex ?? 0;
            fabricStore.updateRegionColor(selectedIndex, c.color, materialIndex);
            
            // 保存最终的区域颜色数组，用于更新URL
            finalRegionColors = strColors;
            
            // 使用更新后的完整颜色数组进行整体变色
            // 重要：使用原始尺寸的 srcImage，不能通过 OSS 缩放
            let baseImgUrl :string = joinUrl(fabricRoot, 'mask', infoResult.data.mask.srcImage!);
            
            // 如果是 SD 模式，变色后需要缩放到 512x512
            const outputSize = style3dStore.resolution === 'sd' ? { width: 512, height: 512 } : undefined;
            
            let colorChangedImage = await getImageColorChangedByMask(
              baseImgUrl, 
              maskImageUrls, 
              maskInfoJsonUrl, 
              strColors,
              outputSize
            )
            if (colorChangedImage){
              localChangedColorUrl = colorChangedImage.toDataURL();
              changeColorUrlMap.set(imgUrl, localChangedColorUrl);
            }
            else{
              hasMask = false;
            }
          }
        }
        if (!hasMask){
          localChangedColorUrl = undefined;
        }
      }
         
      if (localChangedColorUrl){
        let fabricParams: FabricPath = {
          id: item.id,
          xhrPath: localChangedColorUrl,
        };
        
        if (!item.name.endsWith('.sfab')) {
          // 获取原始的物理尺寸配置
          const originalConfig = getFabricPathConfigure(item);
          
          fabricParams = { ...fabricParams, ...originalConfig };
          
          fabricParams.path = fabricParams.xhrPath;
        }

        await changeFabric(fabricParams);
        
        // 重要：changeFabric 会丢失 offset 信息，需要手动恢复
        // 从 info.json 中读取的 offset 信息存储在 item.pos 中
        if (item.pos && item.dpm && item.width && item.height) {
          const { setFabricOffsetX, setFabricOffsetY } = useStyle3d();
          
          if (item.pos.x !== undefined) {
             await setFabricOffsetX(item.pos.x);
          }
          
          if (item.pos.y !== undefined) {
            const height = getMMNum(item.dpm.y, item.height);
            let y = item.pos.y;
            y = height ? Number(((height - (y % height)) / height).toFixed(13)) * 1 : 0;
  
            await setFabricOffsetY(y);
          }
        }
      } else {
        await changeFabricColor(c.color); //利用sdk实现变色
      }
     
      updatedList.push({ ...item, color: c.color });
    }
    relationStore.updateShape(updatedList);

    // 更新URL，传入区域颜色数组（如果有的话）
    updateUrlQuery(found || filteredList[0]!, c, finalRegionColors);

    // 如果原来选中的是materialIndex > 0的面料，恢复选中状态
    // 如果是materialIndex = 0，则清空选中（保持原有行为）
    const currentMaterialIndex = found?.materialIndex ?? 0;
    if (currentMaterialIndex > 0 && originalSelectedFabricId) {
      // 恢复选中状态
      selectionStore.setSelectedFabric(originalSelectedFabricId);
      console.log('[fabric-color] 恢复选中状态:', {
        materialIndex: currentMaterialIndex,
        fabricId: originalSelectedFabricId
      });
    } else {
      // materialIndex = 0 时清空选中（原有行为）
      selectionStore.setSelectedFabric('');
    }
  } else if (selectedFabricId) {
    // 可能是钮扣之类的
    await selectMaterial(selectedFabricId);
    await changeFabricColor(c.color);
    
    // 对于附件类（钮扣等），也恢复选中状态
    selectionStore.setSelectedFabric(originalSelectedFabricId);
  }
};

</script>

<template>
  <div :class="$style.container">
    <div
      v-for="p of currentPalette"
      :key="p.colorNo"
      :style="{ backgroundColor: p.color }"
      style="width: 30px;height:30px;"
      @click="()=>onClickColor(p)"
    />
    <ColorPicker style="width: 30px;height:30px;" @confirm="(color)=>onClickColor({color})">
      <SvgColorPalette style="width: 100%; height: 100%;" />
    </ColorPicker>
  </div>
</template>

<style module>
.container {
  padding: 20px 10px 50px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
</style>
